"use client";

import Link from "next/link";
import { PortalPassBookingSelect } from "./PortalPassBookingSelect";
import { eligibleBookingPasses } from "../lib/portalPasses";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import type { PortalDashboard, PortalEvent, PublicClientPortal } from "../lib/clientPortalApi";
import { hasEventTickets, isAdmissionEvent } from "../lib/eventParticipation";
import { usePortalTranslations } from "../i18n/client";
import { usePortalPreferences } from "./PortalPreferences";
import styles from "./PortalEventRegistration.module.css";

type Props = {
  portal: PublicClientPortal;
  dashboard: PortalDashboard;
  eventUuid: string;
  occurrenceUuid: string;
  loginUrl: string;
};

export function PortalEventRegistration({portal, dashboard, eventUuid, occurrenceUuid, loginUrl}: Props) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  const router = useRouter();
  const item = dashboard.events?.find((entry) => entry.event.uuid === eventUuid);
  const event = item?.event;
  const occurrence = event?.occurrences.find((entry) => entry.uuid === occurrenceUuid);
  const account = dashboard.account ?? dashboard.session?.identity;
  const [contact, setContact] = useState({name: account?.name ?? "", email: account?.email ?? "", phone: "", places: "1"});
  const [passUuid, setPassUuid] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{name: string; units: number} | null>(null);
  const [availability, setAvailability] = useState<{snapshot: PortalEvent; remaining: number | null} | null>(null);
  const submitting = useRef(false);
  const confirmationHeading = useRef<HTMLHeadingElement>(null);
  const contactHeading = useRef<HTMLHeadingElement>(null);
  const backHref = `/${encodeURIComponent(portal.slug)}#events`;
  const admission = event ? isAdmissionEvent(event) : false;
  const textGroup = event && hasEventTickets(event) ? "tickets" : "events";
  const remaining = availability?.snapshot === item ? availability?.remaining ?? null : occurrence?.remaining ?? null;
  const registered = Boolean(confirmation || item?.registeredOccurrenceUuids.includes(occurrenceUuid));
  const confirmed = Boolean(confirmation || (!admission && registered));
  const occupied = occurrence?.capacity != null && remaining !== null ? occurrence.capacity - remaining : occurrence?.occupied ?? 0;
  const past = Boolean(occurrence && new Date(occurrence.startAt).getTime() <= Date.now());
  const full = remaining !== null && remaining < 1;
  const closed = !occurrence || occurrence.cancelled || past || full;
  const units = admission ? Number(contact.places) : 1;
  const maximum = Math.min(remaining ?? 100000, 100000);
  const validQuantity = Number.isInteger(units) && units >= 1 && units <= maximum;
  const eligiblePasses = occurrence && units === 1 ? eligibleBookingPasses(dashboard.passes?.passes ?? [], null, occurrence.startAt, occurrence.endAt) : [];
  const selectedPassUuid = eligiblePasses.some((pass) => pass.uuid === passUuid) ? passUuid : "";

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") router.refresh(); };
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [router]);

  useEffect(() => {
    if (confirmation) confirmationHeading.current?.focus();
  }, [confirmation]);

  async function register(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!item || !event || !occurrence || submitting.current || closed || !item.canRegister
      || (!admission && registered) || !validQuantity) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/api/client-portals/${encodeURIComponent(portal.slug)}/events/${event.uuid}/occurrences/${occurrence.uuid}/registrations`, {
        method: "POST",
        headers: {"content-type": "application/json"},
        body: JSON.stringify({customerName: contact.name.trim(), customerEmail: contact.email.trim(), customerPhone: contact.phone.trim() || null, capacityUnits: units, customerPassUuid: selectedPassUuid || null}),
      });
      if (!response.ok) {
        setError(t(response.status === 429 ? "events.rateLimit" : `${textGroup}.error`));
        router.refresh();
        return;
      }
      setAvailability({snapshot: item, remaining: remaining === null ? null : Math.max(0, remaining - units)});
      setConfirmation({name: contact.name.trim(), units});
      setContact((current) => ({...current, places: "1"}));
      router.refresh();
    } catch {
      setError(t(`${textGroup}.error`));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  if (!event || !occurrence || !item) return <section className={styles.page}>
    <Link href={backHref} className={styles.back}><span aria-hidden="true">←</span>{t("events.backToEvents")}</Link>
    <div className={styles.unavailable}><h1>{t("events.unavailableTitle")}</h1><p>{t("events.unavailableDescription")}</p></div>
  </section>;

  const date = (value: string) => new Intl.DateTimeFormat(locale, {dateStyle: "long", timeStyle: "short", timeZone: event.timezone}).format(new Date(value));
  const calendarDate = new Intl.DateTimeFormat(locale, {dateStyle: "long", timeZone: event.timezone});
  const time = new Intl.DateTimeFormat(locale, {timeStyle: "short", timeZone: event.timezone});
  const sameDay = calendarDate.format(new Date(occurrence.startAt)) === calendarDate.format(new Date(occurrence.endAt));
  const day = new Intl.DateTimeFormat(locale, {day: "numeric", timeZone: event.timezone}).format(new Date(occurrence.startAt));
  const month = new Intl.DateTimeFormat(locale, {month: "short", timeZone: event.timezone}).format(new Date(occurrence.startAt));

  return <section className={styles.page}>
    <Link href={backHref} className={styles.back}><span aria-hidden="true">←</span>{t("events.backToEvents")}</Link>
    <header className={styles.heading}>
      <h1>{t(`${textGroup}.registration`)}</h1>
      <p>{t(admission ? "tickets.registrationIntro" : "events.registrationIntro")}</p>
    </header>
    <div className={styles.layout}>
      <aside className={styles.summary} aria-label={t("events.summary")}>
        <div className={styles.eventIdentity}>
          <div className={styles.dateTile} aria-hidden="true"><strong>{day}</strong><span>{month}</span></div>
          <div><span className={styles.eyebrow}>{portal.name}</span><h2>{event.name}</h2></div>
        </div>
        <dl className={styles.details}>
          <div><dt>{t("events.date")}</dt><dd>{sameDay ? <>
            <time dateTime={occurrence.startAt}>{calendarDate.format(new Date(occurrence.startAt))}</time>
            <span className={styles.endDate}><time dateTime={occurrence.startAt}>{time.format(new Date(occurrence.startAt))}</time> – <time dateTime={occurrence.endAt}>{time.format(new Date(occurrence.endAt))}</time></span>
          </> : <><time dateTime={occurrence.startAt}>{date(occurrence.startAt)}</time><span className={styles.endDate}>– <time dateTime={occurrence.endAt}>{date(occurrence.endAt)}</time></span></>}</dd></div>
          {event.location ? <div><dt>{t("events.location")}</dt><dd>{event.location}</dd></div> : null}
          {event.organizer ? <div><dt>{t("events.organizer")}</dt><dd>{event.organizer}</dd></div> : null}
        </dl>
        <div className={styles.occupancy}>
          <div><span>{t("events.occupancy")}</span><strong>{occurrence.capacity === null ? t("events.unlimited") : `${occupied}/${occurrence.capacity}`}</strong></div>
          {occurrence.capacity !== null && occurrence.capacity > 0 ? <progress aria-label={t("events.occupancy")} max={occurrence.capacity} value={occupied} /> : null}
        </div>
        {event.description ? <p className={styles.description}>{event.description}</p> : null}
      </aside>
      <div className={styles.content}>
        {confirmed ? <section className={styles.confirmation} aria-labelledby="event-confirmation-title">
          <span className={styles.confirmationIcon} aria-hidden="true">✓</span>
          <h2 id="event-confirmation-title" ref={confirmationHeading} tabIndex={-1}>{t(`${textGroup}.registered`)}</h2>
          <p role="status">{t(`${textGroup}.success`)}</p>
          {confirmation ? <div className={styles.receipt}><strong>{confirmation.name}</strong>{admission ? <span>{t("tickets.quantity", {count: confirmation.units})}</span> : null}</div> : null}
          <div className={styles.confirmationActions}>
            {admission && !closed && item.canRegister ? <button type="button" className="primary-button" onClick={() => { setConfirmation(null); requestAnimationFrame(() => contactHeading.current?.focus()); }}>{t("tickets.registerMore")}</button> : null}
            <Link href={backHref} className="secondary-button">{t("events.backToEvents")}</Link>
          </div>
        </section> : closed ? <section className={styles.unavailable}>
          <h2>{t(occurrence.cancelled ? "events.cancelled" : past ? "events.started" : "events.full")}</h2>
          <p>{t("events.closedDescription")}</p>
          <Link href={backHref} className="secondary-button">{t("events.backToEvents")}</Link>
        </section> : !item.canRegister ? <section className={styles.unavailable}>
          <h2>{t(`${textGroup}.unavailable`)}</h2>
          <p>{t(!dashboard.authenticated && portal.loginEnabled ? "events.signInHint" : "events.accessHint")}</p>
          {!dashboard.authenticated && portal.loginEnabled ? <a className="primary-button" href={loginUrl}>{t("events.login")}</a> : <Link className="secondary-button" href={backHref}>{t("events.backToEvents")}</Link>}
        </section> : <form className={styles.form} onSubmit={register} aria-busy={busy}>
          <header className={styles.formHeading}><h2 ref={contactHeading} tabIndex={-1}>{t("events.contactHeading")}</h2><p>{t(admission ? "tickets.buyerDescription" : "events.participantDescription")}</p></header>
          {!dashboard.authenticated ? <p className={styles.guestHint}>{t("events.noAccountRequired")}</p> : null}
          <fieldset className={styles.fields} disabled={busy}>
            <label className={styles.fullWidth}>{t(admission ? "tickets.buyerName" : "events.participantName")}<input autoComplete="name" name="name" value={contact.name} onChange={(e) => setContact({...contact, name: e.target.value})} required maxLength={255} /></label>
            <label>{t("events.email")}<input type="email" autoComplete="email" name="email" value={contact.email} onChange={(e) => setContact({...contact, email: e.target.value})} required maxLength={255} /></label>
            <label>{t("events.phone")}<input type="tel" autoComplete="tel" name="phone" value={contact.phone} onChange={(e) => setContact({...contact, phone: e.target.value})} maxLength={64} /></label>
            {admission ? <div className={styles.quantity}>
              <label htmlFor="event-ticket-quantity">{t(`${textGroup}.places`)}</label>
              <div className={styles.stepper}>
                <button type="button" aria-label={t("events.removeTicket")} disabled={!validQuantity || units <= 1} onClick={() => setContact({...contact, places: String(units - 1)})}>−</button>
                <input id="event-ticket-quantity" name="quantity" type="number" inputMode="numeric" min={1} max={maximum} step={1} value={contact.places} onChange={(e) => setContact({...contact, places: e.target.value})} required />
                <button type="button" aria-label={t("events.addTicket")} disabled={!validQuantity || units >= maximum} onClick={() => setContact({...contact, places: String(units + 1)})}>+</button>
              </div>
            </div> : null}
            <PortalPassBookingSelect passes={eligiblePasses} value={selectedPassUuid} onChange={setPassUuid} disabled={busy} />
          </fieldset>
          {error ? <p role="alert" className={styles.error}>{error}</p> : null}
          <div className={styles.submitArea}>
            <p><span aria-hidden="true">✓</span>{t(`${textGroup}.immediate`)}</p>
            <button className="primary-button" disabled={busy || !validQuantity}>{busy ? <span className={styles.spinner} aria-hidden="true" /> : null}{t(busy ? `${textGroup}.saving` : `${textGroup}.confirm`)}{!busy ? <span aria-hidden="true">→</span> : null}</button>
          </div>
        </form>}
      </div>
    </div>
  </section>;
}
